import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, EyeIcon, RadioIcon, ZapIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const QUALITIES = [{
  label: 'Sharp',
  text: 'One focused idea with no wasted attention.',
  icon: ZapIcon
}, {
  label: 'Highly visual',
  text: 'A short story designed to be understood at a glance.',
  icon: EyeIcon
}, {
  label: 'Adaptive',
  text: 'Reads audience signals and adjusts accordingly.',
  icon: RadioIcon
}];
export function SlideReferenceDesignLogic({}: SlideProps) {
  return <InteriorSlide data-id="element-163" data-mp="1dafy71aslegg">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="flex-shrink-0" data-id="element-164" data-mp="1dazgij4cu4v0">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-165" data-mp="37npd58m3xs63">The format</p>
        <h2 className="mt-3 font-serif text-[clamp(3.5rem,7vw,7.4rem)] font-semibold leading-[0.88] tracking-[-0.05em] text-[var(--fg)]" data-id="element-166" data-mp="3cnaf7rwkqgn1">
          What is a <span className="text-[var(--brand)]" data-id="element-167" data-mp="34wcvbq6eymc0">Snak?</span>
        </h2>
        <p className="mt-7 max-w-5xl text-[clamp(1.2rem,2vw,1.8rem)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-168" data-mp="2zyjpiy90vuc9">
          A sharp, highly visual and short story that engages its audience, reads their signals and adapts accordingly.
        </p>
      </motion.header>

      <div className="my-auto grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-169" data-mp="2lys80njarvti">
        {QUALITIES.map((quality, index) => {
        const Icon = quality.icon;
        return <motion.article key={quality.label} initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.12,
          duration: 0.5
        }} className={`rounded-3xl border p-6 shadow-sm ${index === 1 ? 'border-[var(--brand)]/20 bg-[var(--brand)] text-white' : 'border-[var(--brand)]/10 bg-white text-[var(--fg)]'}`} data-id="element-170" data-mp="2v09tklfh4ppl" data-mpi={index}>
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${index === 1 ? 'bg-white/10 text-[#F8D34A]' : 'bg-[var(--brand-lighter)] text-[var(--brand)]'}`} data-id="element-171" data-mp="2z3phldnlpbp3">
                <Icon className="h-6 w-6" data-id="element-172" data-mp="390ke5r0bd20q" />
              </span>
              <h3 className="mt-5 font-serif text-3xl font-semibold" data-id="element-173" data-mp="38z981kjebi6g">{quality.label}</h3>
              <p className={`mt-3 text-sm font-medium leading-relaxed ${index === 1 ? 'text-white/70' : 'text-[var(--muted)]'}`} data-id="element-174" data-mp="3d0l1601halje">
                {quality.text}
              </p>
            </motion.article>;
      })}
      </div>

      <motion.footer initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.7,
      duration: 0.5
    }} className="flex flex-shrink-0 items-center justify-between gap-8 rounded-3xl bg-[var(--surface)] px-7 py-5" data-id="element-175" data-mp="2pcl0a7agder3">
        <div data-id="element-176" data-mp="1xk4124c8exh1">
          <p className="font-serif text-[clamp(1.35rem,2vw,2rem)] font-semibold text-[var(--fg)]" data-id="element-177" data-mp="21na8vgezswpm">
            Reach out for a short demo.
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--brand)]" data-id="element-178" data-mp="1u3hhk6gu69mj">We promise you’ll be impressed.</p>
        </div>
        <a href="https://www.snakker.io/contact" target="_blank" rel="noreferrer" aria-label="Contact Snakker for a short demo" className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 focus:ring-offset-2" data-id="element-179" data-mp="1p2tsvrg2y4vh">
          <ArrowRightIcon className="h-5 w-5" data-id="element-180" data-mp="11eu06lz7k4e9" />
        </a>
      </motion.footer>
    </InteriorSlide>;
}